import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  format,
  startOfWeek,
  endOfWeek,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  isBefore,
  startOfDay,
} from "date-fns";
import { getDayAvailability } from "../services/api";
import { CalendarViewProps, TimeSlot, Studio } from "../types";

interface TimeSlotDisplay extends TimeSlot {
  display: string;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  studios,
  selectedStudios,
  selectedSlots,
  onSlotSelection,
  onBack,
  initialSelectedDate,
  initialCurrentMonth,
}) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(
    initialCurrentMonth || new Date()
  );
  const [selectedDate, setSelectedDate] = useState<Date>(
    initialSelectedDate || new Date()
  );
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedStartTime, setSelectedStartTime] = useState<string>("");
  const [selectedEndTime, setSelectedEndTime] = useState<string>("");
  const [validEndTimes, setValidEndTimes] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [lastSelectedDate, setLastSelectedDate] = useState<Date | null>(null);
  const [clickedSlots, setClickedSlots] = useState<Set<string>>(new Set());
  const [isSelectingRange, setIsSelectingRange] = useState<boolean>(false);
  const timeSlotsSectionRef = useRef<HTMLDivElement>(null);

  const loadAvailability = useCallback(async (): Promise<void> => {
    if (selectedStudios.length === 0) {
      setAvailableSlots([]);
      setIsInitialLoad(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const data = await getDayAvailability(dateStr, selectedStudios);

      // Flatten all studio slots into a single array
      const allSlots: TimeSlot[] = [];
      Object.values(data.studios || {}).forEach((studioSlots) => {
        allSlots.push(...studioSlots);
      });

      setAvailableSlots(allSlots);
    } catch (err) {
      setError("Failed to load availability");
      console.error("Error loading availability:", err);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [selectedDate, selectedStudios]);

  // Single useEffect to handle availability loading
  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  // Update selected date and month when initial props change (e.g., when coming back from step 3)
  useEffect(() => {
    if (initialSelectedDate) {
      setSelectedDate(initialSelectedDate);
      setLastSelectedDate(initialSelectedDate);
    }
    if (initialCurrentMonth) {
      setCurrentMonth(initialCurrentMonth);
    }
  }, [initialSelectedDate, initialCurrentMonth]);

  // Function to align bottom of iframe to bottom of viewport (with padding)
  const alignIframeBottom = useCallback((): void => {
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: "iframe-align-bottom",
        },
        "*" // In production, replace '*' with the specific origin
      );
    }
  }, []);

  // Align bottom of iframe to bottom of viewport when calendar view initially loads
  useEffect(() => {
    if (selectedStudios.length > 0 && !loading && !isInitialLoad) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        alignIframeBottom();
      }, 100);
    }
  }, [selectedStudios.length, loading, isInitialLoad, alignIframeBottom]);

  // Helper functions that don't depend on state
  const isSlotAvailable = useCallback(
    (slot: TimeSlot): boolean => {
      return availableSlots.some(
        (s) => s.start === slot.start && s.end === slot.end
      );
    },
    [availableSlots]
  );

  const isPastSlot = useCallback((slot: TimeSlot): boolean => {
    const now = new Date();
    const slotStart = new Date(slot.start);
    return slotStart <= now;
  }, []);

  // Helper function to generate time slots for a given date
  const generateTimeSlots = useCallback(
    (date: Date): TimeSlotDisplay[] => {
      // Get business hours from the selected studio
      const selectedStudio = studios.find((studio) =>
        selectedStudios.includes(studio.id)
      );
      const businessHours = selectedStudio?.businessHours;
      const selectedStudioId = selectedStudio?.id || "";

      // Get the day of week
      const dayOfWeek = date
        .toLocaleDateString("en-US", { weekday: "long" })
        .toLowerCase();
      const dayHours = businessHours?.[dayOfWeek];

      // Use configured business hours
      if (!dayHours || !dayHours.isOpen) {
        return []; // Studio is closed on this day or no business hours configured
      }

      const startTime = dayHours.open;
      const endTime = dayHours.close;

      const [startHour] = startTime.split(":").map(Number);
      const [endHour] = endTime.split(":").map(Number);

      // Get the date in local timezone, not UTC
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
      const day = date.getDate();

      // Determine if it's DST (PDT = UTC-7, PST = UTC-8) - same as backend
      const isDST = isDaylightSavingTime(date);
      const utcOffset = isDST ? 7 : 8; // PDT is UTC-7, PST is UTC-8

      // Generate all slots first
      const allSlots: TimeSlotDisplay[] = [];
      for (let hour = startHour; hour < endHour; hour++) {
        // Create start and end times in PST/PDT, then convert to UTC (same as backend)
        const startTime = new Date(
          Date.UTC(year, month - 1, day, hour + utcOffset, 0, 0, 0)
        );
        const endTime = new Date(startTime.getTime() + 60 * 60000); // Add 1 hour

        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const displayEndHour =
          hour + 1 === 0 ? 12 : hour + 1 > 12 ? hour + 1 - 12 : hour + 1;

        allSlots.push({
          start: startTime.toISOString(),
          end: endTime.toISOString(),
          studioId: selectedStudioId,
          display: `${displayHour}:00 ${
            hour < 12 ? "AM" : "PM"
          } - ${displayEndHour}:00 ${hour + 1 < 12 ? "AM" : "PM"}`,
        });
      }

      // Slots are already in chronological order (startHour to endHour)
      return allSlots;
    },
    [studios, selectedStudios]
  );

  // Helper function to check consecutive availability
  const checkConsecutiveAvailability = useCallback(
    (startTime: string, endTime: Date, allSlots: TimeSlotDisplay[]) => {
      // Check if all slots from startTime to endTime are available
      const start = new Date(startTime);
      let current = new Date(start);

      const findSlotAtTime = (time: Date) => {
        return allSlots.find((slot) => slot.start === time.toISOString());
      };

      // Check all slots that start before endTime
      while (current < endTime) {
        const slotAtCurrentTime = findSlotAtTime(current);

        // If no slot exists at this time, we've reached the end of business hours
        if (!slotAtCurrentTime) return false;

        // Check if this slot is available and not in the past
        if (
          !isSlotAvailable(slotAtCurrentTime) ||
          isPastSlot(slotAtCurrentTime)
        ) {
          return false;
        }

        // Move to next hour
        current = new Date(current.getTime() + 60 * 60000);
      }

      // Also check the slot that ends at endTime (the final slot in the range)
      const slotEndingAtEndTime = allSlots.find(
        (slot) => slot.end === endTime.toISOString()
      );

      if (slotEndingAtEndTime) {
        // If there's a slot ending at endTime, it must be available and not in the past
        if (
          !isSlotAvailable(slotEndingAtEndTime) ||
          isPastSlot(slotEndingAtEndTime)
        ) {
          return false;
        }
      }

      return true;
    },
    [isSlotAvailable, isPastSlot]
  );

  // Helper function to get valid end times based on selected start time
  const getValidEndTimes = useCallback(
    (startTime: string): TimeSlot[] => {
      if (!startTime) return [];

      const startSlot = availableSlots.find((slot) => slot.start === startTime);
      if (!startSlot) return [];

      const validEndTimes: TimeSlot[] = [];
      const selectedStudioId =
        studios.find((s) => selectedStudios.includes(s.id))?.id || "";
      const startDate = new Date(startTime);

      // Generate one-hour increments starting from the next hour after start time
      let currentEndTime = new Date(startDate.getTime() + 60 * 60000); // Start from next hour

      // Get all possible time slots for the day to check availability
      const allSlots = generateTimeSlots(selectedDate);

      const findSlotThatEndsAt = (time: Date) => {
        return allSlots.find((slot) => slot.end === time.toISOString());
      };

      while (true) {
        // Check if there's a slot that ends at currentEndTime
        const slotThatEndsAtCurrentTime = findSlotThatEndsAt(currentEndTime);

        // If no slot ends at this time, we've reached beyond business hours
        if (!slotThatEndsAtCurrentTime) break;

        // Check if all slots from startTime to currentEndTime are available
        if (checkConsecutiveAvailability(startTime, currentEndTime, allSlots)) {
          // Create a slot representing the end time (currentEndTime)
          validEndTimes.push({
            start: currentEndTime.toISOString(),
            end: new Date(currentEndTime.getTime() + 60 * 60000).toISOString(),
            studioId: selectedStudioId,
          });
        } else {
          // If this slot is not available, we can't continue the consecutive booking
          break;
        }

        // Move to next hour
        currentEndTime = new Date(currentEndTime.getTime() + 60 * 60000);
      }

      return validEndTimes;
    },
    [
      availableSlots,
      selectedDate,
      generateTimeSlots,
      checkConsecutiveAvailability,
      studios,
      selectedStudios,
    ]
  );

  // Update valid end times when start time changes
  useEffect(() => {
    if (selectedStartTime) {
      setValidEndTimes(getValidEndTimes(selectedStartTime));
    } else {
      setValidEndTimes([]);
    }
  }, [selectedStartTime, getValidEndTimes]);

  // Sync selected times with prop changes (e.g., when going back)
  useEffect(() => {
    if (selectedSlots.length > 0) {
      const firstSlot = selectedSlots[0];
      const lastSlot = selectedSlots[selectedSlots.length - 1];
      const slotDate = new Date(firstSlot.start);

      // Only restore time selections if they're for the currently selected date
      if (isSameDay(slotDate, selectedDate)) {
        setSelectedStartTime(firstSlot.start);
        setSelectedEndTime(lastSlot.end);
        // Track the date where times were actually selected
        setLastSelectedDate(slotDate);
      } else {
        // Clear time selections if the slots are for a different date
        setSelectedStartTime("");
        setSelectedEndTime("");
        setLastSelectedDate(null);
        setClickedSlots(new Set());
      }
    } else {
      setSelectedStartTime("");
      setSelectedEndTime("");
      setLastSelectedDate(null);
      setClickedSlots(new Set());
    }
  }, [selectedSlots, selectedDate]);

  // Sync clicked slots when dropdown values change
  // This effect ensures that when dropdowns are used, the visual slot selection updates
  useEffect(() => {
    // Only update if we have both start and end times
    if (selectedStartTime && selectedEndTime) {
      // Generate all slots for the day
      const allSlots = generateTimeSlots(selectedDate);

      // Generate all slots between start and end
      const slotsInRange: string[] = [];
      const startDate = new Date(selectedStartTime);
      const endDate = new Date(selectedEndTime);
      let currentTime = new Date(startDate);

      while (currentTime < endDate) {
        const slotStart = currentTime.toISOString();
        // Check if this slot exists in allSlots
        const slot = allSlots.find((s) => s.start === slotStart);
        if (slot) {
          slotsInRange.push(slotStart);
        }
        currentTime = new Date(currentTime.getTime() + 60 * 60000);
      }

      setClickedSlots(new Set(slotsInRange));
    } else if (!selectedStartTime && !selectedEndTime) {
      // Clear selection if both are empty
      setClickedSlots(new Set());
    }
  }, [selectedStartTime, selectedEndTime, selectedDate, generateTimeSlots]);

  // Helper function to check if a time range is valid
  const isValidTimeRange = (startTime: string, endTime: string): boolean => {
    if (!startTime || !endTime) return false;

    const start = new Date(startTime);
    const end = new Date(endTime);

    // End time must be after start time
    if (end <= start) return false;

    // Check if all slots in the range are available
    const slots = generateConsecutiveSlots(startTime, endTime);
    return slots.every((slot) => isSlotAvailable(slot) && !isPastSlot(slot));
  };

  // Helper function to generate consecutive slots between start and end times
  const generateConsecutiveSlots = (
    startTime: string,
    endTime: string
  ): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const start = new Date(startTime);
    const end = new Date(endTime);

    const selectedStudioId =
      studios.find((s) => selectedStudios.includes(s.id))?.id || "";

    let currentTime = new Date(start);

    // Generate slots until we reach the end time
    while (currentTime < end) {
      const nextHour = new Date(currentTime.getTime() + 60 * 60000);

      slots.push({
        start: currentTime.toISOString(),
        end: nextHour.toISOString(),
        studioId: selectedStudioId,
      });

      currentTime = nextHour;
    }

    return slots;
  };

  const handleStartTimeChange = (startTime: string): void => {
    setSelectedStartTime(startTime);

    // Always automatically set end time to one hour after start time
    const startDate = new Date(startTime);
    const endDate = new Date(startDate.getTime() + 60 * 60000); // Add 1 hour
    setSelectedEndTime(endDate.toISOString());

    setValidEndTimes(getValidEndTimes(startTime));
    // Track that times are being selected on this date
    setLastSelectedDate(selectedDate);

    // Update clicked slots to include both start and end slots
    const newClickedSlots = new Set<string>();
    let current = new Date(startTime);
    const end = new Date(endDate);

    while (current < end) {
      newClickedSlots.add(current.toISOString());
      current = new Date(current.getTime() + 60 * 60000);
    }
    setClickedSlots(newClickedSlots);

    // Keep range selection active to allow extending
    setIsSelectingRange(true);

    // Align bottom of iframe to bottom of viewport when selecting times
    alignIframeBottom();
  };

  const handleEndTimeChange = (endTime: string): void => {
    setSelectedEndTime(endTime);
    // Update valid end times to reflect the new end time (for dropdown)
    setValidEndTimes(getValidEndTimes(selectedStartTime));
    // Track that times are being selected on this date
    setLastSelectedDate(selectedDate);
    // Keep range selection active after setting end time to allow extending
    setIsSelectingRange(true);

    // Align bottom of iframe to bottom of viewport when selecting times
    alignIframeBottom();
  };

  const handleNext = (): void => {
    // If there are already selected slots, allow proceeding to step 3
    if (selectedSlots.length > 0) {
      onSlotSelection(selectedSlots);
      return;
    }

    // If no previously selected slots, require current day selection
    if (!selectedStartTime || !selectedEndTime) {
      alert("Please select both start and end times");
      return;
    }

    if (!isValidTimeRange(selectedStartTime, selectedEndTime)) {
      alert(
        "Please select a valid time range with consecutive available slots"
      );
      return;
    }

    // Convert start/end times to TimeSlot[] format for backward compatibility
    const slots = generateConsecutiveSlots(selectedStartTime, selectedEndTime);
    onSlotSelection(slots);
  };

  // Get available start times for the dropdown
  const getAvailableStartTimes = (): TimeSlot[] => {
    const allSlots = generateTimeSlots(selectedDate);
    return allSlots.filter(
      (slot) => isSlotAvailable(slot) && !isPastSlot(slot)
    );
  };

  // Helper function to determine if a date is in daylight saving time for US Pacific timezone
  // Uses the Intl API for maximum accuracy and future-proofing
  const isDaylightSavingTime = (date: Date): boolean => {
    // Use Intl API to get timezone information for America/Los_Angeles
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      timeZoneName: "short",
    });

    // Get the timezone name (PST or PDT)
    const parts = formatter.formatToParts(date);
    const timeZoneName = parts.find(
      (part) => part.type === "timeZoneName"
    )?.value;

    return timeZoneName === "PDT";
  };

  const navigateMonth = (direction: number): void => {
    setCurrentMonth((prev) => {
      const today = new Date();
      const currentMonthStart = startOfMonth(prev);
      const todayMonthStart = startOfMonth(today);

      // Prevent going to past months
      if (direction < 0 && currentMonthStart <= todayMonthStart) {
        return prev;
      }

      return direction > 0 ? addMonths(prev, 1) : subMonths(prev, 1);
    });
  };

  const scrollToTimeSlots = (): void => {
    // Wait a bit for the availability to load and UI to update
    setTimeout(() => {
      if (timeSlotsSectionRef.current) {
        timeSlotsSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      // Align bottom of iframe to bottom of viewport when selecting dates
      alignIframeBottom();
    }, 300); // Small delay to allow availability to load
  };

  const handleDateSelect = (date: Date): void => {
    // Prevent selecting past dates
    if (isBefore(date, startOfDay(new Date()))) {
      return;
    }

    // Only clear selections if selecting a different date
    const isNewDate = !isSameDay(date, selectedDate);
    if (isNewDate) {
      setSelectedStartTime("");
      setSelectedEndTime("");
      setValidEndTimes([]);
      setClickedSlots(new Set());
      setIsSelectingRange(false);
      setLastSelectedDate(null);

      // Scroll to time slots section when selecting a new date
      scrollToTimeSlots();
    }

    setSelectedDate(date);
  };

  const getCalendarDays = (): Date[] => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const startOfFirstWeek = startOfWeek(start);
    const endOfLastWeek = endOfWeek(end);

    return eachDayOfInterval({
      start: startOfFirstWeek,
      end: endOfLastWeek,
    });
  };

  const formatTime = (date: string): string => {
    return format(parseISO(date), "h:mm a");
  };

  // Get selected studio details
  const selectedStudio: Studio | undefined = studios.find((studio) =>
    selectedStudios.includes(studio.id)
  );

  // Check if we can navigate to previous month
  const canNavigatePrevious = () => {
    const today = new Date();
    const currentMonthStart = startOfMonth(currentMonth);
    const todayMonthStart = startOfMonth(today);
    return currentMonthStart > todayMonthStart;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-primary-900 mb-4">
          Select Time Slots
        </h2>
        <p className="text-base sm:text-lg text-primary-600 mb-2">
          Choose a date and time slots for your booking
        </p>
      </div>

      {/* Selected Studio Display */}
      {selectedStudio && (
        <div className="mb-6 sm:mb-8 bg-white rounded-lg shadow-md border border-primary-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 mb-3 sm:mb-0">
              <h3 className="text-lg sm:text-xl font-semibold text-primary-900 mb-1">
                {selectedStudio.name}
              </h3>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-primary-100 text-primary-800">
                Selected Studio
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calendar Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigateMonth(-1)}
              disabled={!canNavigatePrevious()}
              className={`p-2 border rounded-lg transition-colors ${
                canNavigatePrevious()
                  ? "border-primary-300 hover:bg-primary-50 text-primary-700 hover:text-primary-900"
                  : "border-gray-200 text-gray-300 cursor-not-allowed"
              }`}
              aria-label="Previous Month"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <h3 className="text-xl text-primary-900">
              {format(currentMonth, "MMMM yyyy")}
            </h3>

            <button
              onClick={() => navigateMonth(1)}
              className="p-2 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors text-primary-700 hover:text-primary-900"
              aria-label="Next Month"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
              <div
                key={`${day}-${index}`}
                className="p-2 text-center text-sm font-medium text-primary-700"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {getCalendarDays().map((day, index) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              const isPastDate = isBefore(day, startOfDay(new Date()));
              const isDisabled = isPastDate;
              const hasSelectedTimes =
                lastSelectedDate && isSameDay(day, lastSelectedDate);

              return (
                <button
                  key={index}
                  onClick={() => !isDisabled && handleDateSelect(day)}
                  disabled={isDisabled}
                  className={`text-sm transition-colors ${
                    isSelected
                      ? "bg-black text-white rounded-full w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 flex items-center justify-center mx-auto"
                      : hasSelectedTimes
                      ? "bg-primary-200 text-primary-900 font-semibold rounded-lg border-2 border-primary-400 w-5 h-5 sm:w-8 sm:h-8 flex items-center justify-center mx-auto"
                      : isToday
                      ? "bg-primary-100 text-primary-900 font-semibold rounded-lg w-5 h-5 sm:w-8 sm:h-8 flex items-center justify-center mx-auto"
                      : isCurrentMonth && !isDisabled
                      ? "text-primary-900 hover:bg-primary-50 rounded-lg w-5 h-5 sm:w-8 sm:h-8 flex items-center justify-center mx-auto"
                      : !isCurrentMonth && !isDisabled
                      ? "text-primary-400 rounded-lg w-5 h-5 sm:w-8 sm:h-8 flex items-center justify-center mx-auto"
                      : "text-gray-300 cursor-not-allowed rounded-lg w-5 h-5 sm:w-8 sm:h-8 flex items-center justify-center mx-auto"
                  }`}
                  title={
                    isPastDate
                      ? "Cannot select past dates"
                      : hasSelectedTimes
                      ? "Times selected on this day"
                      : ""
                  }
                >
                  {format(day, "d")}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Slots Section */}
        <div
          ref={timeSlotsSectionRef}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="mb-6">
            <h3 className="text-lg text-primary-900 mb-2">
              {format(selectedDate, "EEEE, MMMM d")}
            </h3>
            <p className="text-xs text-primary-600">
              TIME ZONE: PACIFIC TIME (GMT-07:00)
            </p>
          </div>

          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-primary-600">Loading availability...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <div className="text-primary-700 text-xl mb-2">⚠️</div>
              <p className="text-primary-700 mb-4">{error}</p>
              <button
                onClick={loadAvailability}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          )}

          {!loading &&
            !error &&
            !isInitialLoad &&
            selectedStudios.length > 0 && (
              <>
                {/* Visual Availability Indicator */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-primary-700 mb-3">
                    Availability for {format(selectedDate, "MMM d")}
                  </h4>
                  <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap">
                    {generateTimeSlots(selectedDate).map((timeSlot, index) => {
                      const isAvailable = isSlotAvailable(timeSlot);
                      const isPast = isPastSlot(timeSlot);
                      const isInRange =
                        selectedStartTime &&
                        selectedEndTime &&
                        timeSlot.start >= selectedStartTime &&
                        timeSlot.end <= selectedEndTime;
                      const isSlotClicked = clickedSlots.has(timeSlot.start);

                      const handleSlotClick = () => {
                        if (!isAvailable || isPast) return;

                        // Check if this slot is already selected - if so, deselect it
                        if (isSlotClicked) {
                          // Remove this specific slot from the selection
                          const newClickedSlots = new Set(clickedSlots);
                          newClickedSlots.delete(timeSlot.start);
                          setClickedSlots(newClickedSlots);

                          // If this was the only selected slot, clear everything
                          if (newClickedSlots.size === 0) {
                            setSelectedStartTime("");
                            setSelectedEndTime("");
                            setValidEndTimes([]);
                            setIsSelectingRange(false);
                            return;
                          }

                          // If this was the start time, find a new start time
                          if (selectedStartTime === timeSlot.start) {
                            const sortedSlots =
                              Array.from(newClickedSlots).sort();
                            const newStartTime = sortedSlots[0];
                            setSelectedStartTime(newStartTime);

                            // If we had an end time, we need to recalculate it
                            if (selectedEndTime) {
                              const newEndTime =
                                sortedSlots[sortedSlots.length - 1];
                              // Convert to end time by adding 1 hour
                              const endTime = new Date(newEndTime);
                              endTime.setHours(endTime.getHours() + 1);
                              setSelectedEndTime(endTime.toISOString());
                            }
                          }
                          // If this was the end time, find a new end time
                          else if (
                            selectedEndTime &&
                            timeSlot.end === selectedEndTime
                          ) {
                            const sortedSlots =
                              Array.from(newClickedSlots).sort();
                            const newEndTime =
                              sortedSlots[sortedSlots.length - 1];
                            // Convert to end time by adding 1 hour
                            const endTime = new Date(newEndTime);
                            endTime.setHours(endTime.getHours() + 1);
                            setSelectedEndTime(endTime.toISOString());
                          }

                          return;
                        }

                        // Check if we have an existing range and can extend it
                        if (selectedStartTime && selectedEndTime) {
                          const clickedTime = new Date(
                            timeSlot.start
                          ).getTime();
                          const currentEndTime = new Date(
                            selectedEndTime
                          ).getTime();

                          // If clicking the slot immediately after the current end, extend the range
                          if (clickedTime === currentEndTime) {
                            // Extend the range by this one slot
                            handleEndTimeChange(timeSlot.end);

                            // Update clicked slots to include the new slot
                            const newClickedSlots = new Set(clickedSlots);
                            newClickedSlots.add(timeSlot.start);
                            setClickedSlots(newClickedSlots);
                            return;
                          }
                        }

                        // If we're in range selection mode and have a start time, this is the end
                        if (isSelectingRange && selectedStartTime) {
                          const clickedTime = new Date(
                            timeSlot.start
                          ).getTime();
                          const startTime = new Date(
                            selectedStartTime
                          ).getTime();

                          // If clicking before the start time, make this the new start
                          if (clickedTime < startTime) {
                            handleStartTimeChange(timeSlot.start);
                            return;
                          }

                          // Validate that all slots from start to clicked slot are available
                          const slotsToCheck = generateConsecutiveSlots(
                            selectedStartTime,
                            timeSlot.end
                          );
                          const allSlotsAvailable = slotsToCheck.every(
                            (slot) => isSlotAvailable(slot) && !isPastSlot(slot)
                          );

                          if (!allSlotsAvailable) {
                            // If range is invalid, just set this as the new start time
                            handleStartTimeChange(timeSlot.start);
                            return;
                          }

                          // Valid range: set end time and highlight all slots
                          const newClickedSlots = new Set<string>();
                          let current = new Date(selectedStartTime);
                          const end = new Date(timeSlot.end);

                          while (current < end) {
                            newClickedSlots.add(current.toISOString());
                            current = new Date(current.getTime() + 60 * 60000);
                          }

                          setClickedSlots(newClickedSlots);
                          handleEndTimeChange(timeSlot.end);
                          return;
                        }

                        // First click: set as start time and automatically set end time
                        handleStartTimeChange(timeSlot.start);

                        // Automatically set end time to one hour after start time
                        const startDate = new Date(timeSlot.start);
                        const endDate = new Date(
                          startDate.getTime() + 60 * 60000
                        ); // Add 1 hour
                        setSelectedEndTime(endDate.toISOString());

                        // Update clicked slots to include both start and end slots
                        const newClickedSlots = new Set<string>();
                        let current = new Date(timeSlot.start);
                        const end = new Date(endDate);

                        while (current < end) {
                          newClickedSlots.add(current.toISOString());
                          current = new Date(current.getTime() + 60 * 60000);
                        }
                        setClickedSlots(newClickedSlots);

                        setIsSelectingRange(true);
                      };

                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={handleSlotClick}
                          disabled={!isAvailable || isPast}
                          className={`w-12 h-8 rounded text-xs flex items-center justify-center font-medium transition-all ${
                            isInRange
                              ? "bg-primary-600 text-white"
                              : isSlotClicked
                              ? "bg-primary-500 text-white ring-2 ring-primary-400"
                              : isAvailable && !isPast
                              ? "bg-primary-100 text-primary-800 border border-primary-300 hover:bg-primary-200 hover:border-primary-400 cursor-pointer"
                              : isPast
                              ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                              : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                          }`}
                        >
                          {formatTime(timeSlot.start).replace(":00", "")}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time Selection Dropdowns */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary-700 mb-2">
                      Start Time *
                    </label>
                    <select
                      value={selectedStartTime}
                      onChange={(e) => handleStartTimeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    >
                      <option value="">Select start time</option>
                      {getAvailableStartTimes().map((slot, index) => (
                        <option key={index} value={slot.start}>
                          {formatTime(slot.start)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary-700 mb-2">
                      End Time *
                    </label>
                    <select
                      value={selectedEndTime}
                      onChange={(e) => handleEndTimeChange(e.target.value)}
                      disabled={!selectedStartTime}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm ${
                        !selectedStartTime
                          ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                          : "border-primary-300"
                      }`}
                    >
                      <option value="">Select end time</option>
                      {validEndTimes.map((slot, index) => (
                        <option key={index} value={slot.start}>
                          {formatTime(slot.start)}
                        </option>
                      ))}
                    </select>
                    {!selectedStartTime}
                  </div>
                </div>
              </>
            )}

          {!loading &&
            !error &&
            !isInitialLoad &&
            selectedStudios.length === 0 && (
              <div className="text-center py-8">
                <div className="text-primary-500 text-xl mb-2">📅</div>
                <p className="text-primary-600">
                  Please select a studio to view available time slots
                </p>
              </div>
            )}
        </div>
      </div>

      {/* Selected Time Range Summary */}
      {selectedStartTime && selectedEndTime && (
        <div className="mt-6 bg-primary-50 border border-primary-200 rounded-lg p-4">
          <h4 className="font-semibold text-primary-900 mb-2">
            Selected Time Range:
          </h4>
          <div className="text-sm text-primary-800">
            {format(parseISO(selectedStartTime), "MMM d, yyyy")} -{" "}
            {formatTime(selectedStartTime)} to {formatTime(selectedEndTime)}
            <br />
            <span className="text-xs text-primary-600">
              (
              {Math.ceil(
                (new Date(selectedEndTime).getTime() -
                  new Date(selectedStartTime).getTime()) /
                  (60 * 60 * 1000)
              )}{" "}
              hours)
            </span>
          </div>
        </div>
      )}

      {/* Mobile and Desktop: Side-by-side layout */}
      <div className="flex flex-row justify-between items-center mt-6 sm:mt-8 space-x-2 sm:space-x-4">
        <button
          onClick={onBack}
          className="px-3 sm:px-6 py-2.5 sm:py-3 border border-primary-300 rounded-lg text-primary-700 hover:bg-primary-50 active:bg-primary-100 transition-all duration-200 text-sm sm:text-base font-medium flex items-center justify-center space-x-1 sm:space-x-2 touch-manipulation flex-1 sm:flex-none"
        >
          <span className="text-sm sm:text-base">←</span>
          <span className="hidden xs:inline">Back to Studios</span>
          <span className="xs:hidden">Back</span>
        </button>

        <button
          onClick={handleNext}
          disabled={
            selectedSlots.length === 0 &&
            (!selectedStartTime || !selectedEndTime)
          }
          className={`px-3 sm:px-8 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base flex items-center justify-center space-x-1 sm:space-x-2 touch-manipulation flex-1 sm:flex-none ${
            selectedSlots.length === 0 &&
            (!selectedStartTime || !selectedEndTime)
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 shadow-lg hover:shadow-xl"
          }`}
        >
          <span className="hidden xs:inline">Booking Details</span>
          <span className="xs:hidden">Details</span>
          <span className="text-sm sm:text-base">→</span>
        </button>
      </div>
    </div>
  );
};

export default CalendarView;
